/**
 * GitHubAuthBar — Compact auth bar for the chat sidebar.
 *
 * Shows "Sign in with GitHub" when unauthenticated (with value prop),
 * or the user's GitHub avatar/login + Disconnect when authenticated.
 * Restores auth state from cookie on mount.
 */

import { useEffect } from 'react';
import { makeStyles, tokens, Button, Text } from '@coreai/fluentui-react';
import { PersonAccountsFilled, SignOutFilled } from '@fluentui/react-icons';
import { useGitHubAuthStore } from '@/stores/githubAuthStore';
import { useAuth } from '@/auth/useAuth';

const useStyles = makeStyles({
  hintText: {
    color: tokens.colorNeutralForeground3,
  },
  authBar: {
    paddingBlock: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '36px',
    flexShrink: 0,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    overflow: 'hidden',
  },
  avatar: {
    width: '20px',
    height: '20px',
    borderRadius: tokens.borderRadiusCircular,
    flexShrink: 0,
  },
  loginText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export const GitHubAuthBar = () => {
  const styles = useStyles();
  const { getOttoToken } = useAuth();
  const {
    isAuthenticated,
    user,
    isLoading,
    login,
    logout,
    checkStatus,
  } = useGitHubAuthStore();

  // Restore auth state from cookie on mount
  useEffect(() => {
    checkStatus(getOttoToken);
    // getOttoToken is excluded from deps — its identity changes on every render
    // (due to MSAL account/tenant deps) but the function's behavior is stable.
    // Including it would cause an infinite re-render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkStatus]);

  if (isLoading) {
    return null; // Don't flash UI while checking cookie
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.authBar}>
        <Text size={200} className={styles.hintText}>
          Unlock more AI models
        </Text>
        <Button
          icon={<PersonAccountsFilled />}
          appearance="subtle"
          size="small"
          onClick={login}
        >
          Sign in with GitHub
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.authBar}>
      <div className={styles.userInfo}>
        {user?.avatarUrl && (
          <img
            src={user.avatarUrl}
            alt={`${user.login ?? 'GitHub user'} avatar`}
            className={styles.avatar}
          />
        )}
        <Text size={200} className={styles.loginText}>
          {user?.login ?? 'GitHub'}
        </Text>
      </div>
      <Button
        icon={<SignOutFilled />}
        appearance="subtle"
        size="small"
        onClick={() => logout(getOttoToken)}
      >
        Disconnect
      </Button>
    </div>
  );
};
